import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { enumReadSchema } from "../../core/enum-read.schema";

/**
 * Why an app can or cannot deploy right now — the ONE thing standing in the
 * way, named.
 *
 * This exists because "cannot deploy" had no name. An app whose git project
 * was never attached rendered as `Never deployed`, byte-identical to a
 * correctly-wired app nobody had pushed to yet, and a push to its repository
 * built nothing without saying so. The absence of a link was represented by
 * the absence of a value, which is exactly what nothing can render.
 *
 * DERIVED, never stored. There is no column: the answer is a function of the
 * app's git project as it stands at read time, so it cannot go stale and it
 * cannot disagree with the project row it is computed from.
 *
 * Deliberately three values, not four. `SOURCE_NOT_READY` covers a project
 * that is PENDING, FAILED or ARCHIVED without re-encoding which — the caller
 * reads `VibeGitProjectStatus` for that, and it is right there. Splitting it
 * would clone that enum into a second one that can drift out of agreement
 * with the first.
 */
export const VIBE_APP_DEPLOYABILITY_VALUES = [
  /// A live git project is attached and READY. A push to its deploy branch
  /// builds.
  "DEPLOYABLE",
  /// No git project resolves for this app. Either none was ever attached, or
  /// the one it points at was deleted — the app reads the same either way,
  /// because every project read excludes soft-deleted rows. This is the state
  /// that used to be invisible.
  "NO_SOURCE_ATTACHED",
  /// A project is attached but is not READY: still materializing on the
  /// tenant's git host (PENDING), failed to (FAILED), or archived. The
  /// project's own status says which.
  "SOURCE_NOT_READY"
] as const;

export type VibeAppDeployability = (typeof VIBE_APP_DEPLOYABILITY_VALUES)[number];

export const VibeAppDeployabilitySchema = z.enum(VIBE_APP_DEPLOYABILITY_VALUES);

/** `VibeAppDeployabilitySchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeAppDeployabilityReadSchema = enumReadSchema(VIBE_APP_DEPLOYABILITY_VALUES);

type VibeGitProjectStatusLiteral = (typeof DbEnum.VIBE_GIT_PROJECT_STATUS_VALUES)[number];

/**
 * Resolve an app's deployability from the status of the git project that
 * resolves for it, or `null` when none does.
 *
 * Pure and total. `null` is the load-bearing input: callers MUST pass null for
 * a project that is absent OR soft-deleted OR in another org, because those
 * are the same fact to every other reader in the system, and a fourth opinion
 * here is how "attached" would come to mean something different depending on
 * who asked.
 *
 * The status switch is exhaustive, so a new `VibeGitProjectStatus` is a
 * compile error here rather than a silent fall into "not ready" — a status
 * meaning "ready in a new way" must be classified deliberately.
 */
export function resolveVibeAppDeployability(
  gitProjectStatus: VibeGitProjectStatusLiteral | null
): VibeAppDeployability {
  if (gitProjectStatus === null) return "NO_SOURCE_ATTACHED";

  switch (gitProjectStatus) {
    case "READY":
      return "DEPLOYABLE";
    case "PENDING":
    case "FAILED":
    case "ARCHIVED":
      return "SOURCE_NOT_READY";
    default: {
      const exhaustive: never = gitProjectStatus;
      void exhaustive;
      return "SOURCE_NOT_READY";
    }
  }
}
